import React from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

export default function PrivacyPolicy() {
  return (
    <>
      <div className="max-w-screen-md mx-auto mt-20 mb-14 font-brand text-gray-700">
        <div>
          <h1 className="text-4xl">Privacy Policy</h1>
          <h4 className="text-xl">Effective Date: January 1, 2020</h4>
        </div>
        <div className="prose text-gray-800">
          <p>
            {' '}
            We take the privacy of our customers seriously and are committed to
            protecting your privacy. This policy explains how we collect, use
            and transfer your personal data, and your rights in relation to the
            personal data collected and stored by us when you use our website or
            otherwise engage with our services.
          </p>
          <h3> This policy sets out the following:</h3>
          <ul>
            <li>What personal data we collect about you and how;</li>
            <li>How the data is used;</li>
            <li>Our legal basis for collecting your information;&nbsp;</li>
            <li>Who we share your data with;</li>
            <li>Where we transfer your information;&nbsp;</li>
            <li>How long we retain your information;</li>
            <li>Your rights and choices in relation to the data held by us;</li>
            <li>How to make a complaint in relation to the data held by us;</li>
            <li>How we use cookies on our website; and</li>
            <li>
              How to contact us with any queries in relation to this notice, or
              the personal data held by us.
            </li>
          </ul>
          <p>
            <strong>Who is ContractorBonds.com?</strong>
          </p>
          <p>
            ContractorBonds.com provides efficient, innovative, and informed
            contractor bond solutions nationwide.
          </p>
          <p>
            For the purposes of European data protection laws, if you are
            visiting our website www.contractorbonds.com (our "Website") or
            otherwise engaging with our services from the European Economic Area
            (or "EEA"), the data controller of your information is
            SuretyBonds.com.
          </p>
          <p>
            <strong>What is personal data?</strong>
          </p>
          <p>
            In this privacy policy, references to “personal information” or
            “personal data” are references to information that relates to an
            identified or identifiable individual.&nbsp; Some examples of
            personal data are your name, address and telephone number but it may
            also include information such as your IP address and location, in
            certain jurisdictions.
          </p>
          <p>
            <strong>What personal data do we collect?</strong>
          </p>
          <p>
            We collect personal data that you provide to us when you sign up for
            our services, such as your contact information and financial
            information.&nbsp; We may also collect commercial information based
            on how you interact with our services, such as the products or
            services you’ve purchased or other Internet or network activity,
            such as your Website browsing history or mobile device
            information.&nbsp;
          </p>
          <p>
            Below are some more details on the type of personal data we collect.
          </p>
          <p>INFORMATION THAT IS PROVIDED BY YOU</p>
          <p>
            In order to provide services to you, we may ask you to provide
            personal information.&nbsp; This may include, amongst other things,
            your name, email address, postal address, telephone number, gender,
            date of birth, passport number, bank account details, credit history
            and claims history, citizenship status, marital status depending on
            the service you are seeking.&nbsp; The personal information that you
            are asked to provide, and the reasons why you are asked to provide
            it, will be made clear to you at the point at which we ask you for
            it.
          </p>
          <p>
            Some of the information that you provide may be ‘Special category’
            (or ‘sensitive’) personal data”.&nbsp; Sensitive personal data
            relates to your racial or ethnic origin, political opinions,
            religious or philosophical beliefs, or trade union membership,
            genetic data, biometric data for the purpose of uniquely identifying
            a natural person, data concerning health or data concerning a
            natural person’s sex life or sexual orientation, and can sometimes
            be inferred from other, non-sensitive, information that you have
            provided.
          </p>
          <p>
            <strong>Information that we collect on our website&nbsp;</strong>
          </p>
          <p>
            When you visit our Website, we will seek your consent to collect
            certain information from your device.&nbsp; In some countries,
            including countries in the EEA, this information may be considered
            personal information under applicable data protection laws.
          </p>
          <p>
            Specifically, the information we are seeking to collect includes
            information like your IP address, device type, unique device
            identification numbers, browser type, broad geographic location
            (e.g. country or city-level location) and other technical
            information.&nbsp; We may also collect information about how your
            device has interacted with our Website, including the pages accessed
            and links clicked.&nbsp;&nbsp;
          </p>
          <p>
            Collecting this information enables us to better understand visitors
            to our Website, where they come from, and what content on our
            Website is of interest to them.&nbsp; We use this information for
            our internal analytics purposes and to improve the quality and
            relevance of our Website to our visitors.
          </p>
          <p>
            Some of this information may be collected using cookies and similar
            tracking technology, as explained further under the heading
            “Cookies” below.
          </p>
          <p>
            <strong>Information that we obtain from third party sources</strong>
          </p>
          <p>
            From time to time, we may receive personal information about you
            from third party sources but only where we have checked that these
            third parties either have your consent or are otherwise legally
            permitted or required to disclose your personal information to us.
          </p>
          <p>
            For example, if you are an individual who is obtaining insurance
            from us via an insurance broker, we may obtain data about you from
            your broker in order to help us prepare your quote and/or your
            insurance policy.&nbsp; For information about how your broker uses
            and shares your personal data, please refer to the broker’s own
            privacy statement.
          </p>
          <p>
            We may, where we are legally permitted to, also collect personal
            data from the following sources in order to provide services to you:
          </p>
          <ul>
            <li>Credit reference agencies;</li>
            <li>Anti-fraud and other databases;</li>
            <li>Government agencies;</li>
            <li>Electoral register;</li>
            <li>Court judgments;&nbsp;</li>
            <li>Sanctions lists;</li>
            <li>Family members; and</li>
            <li>
              In the event of an insurance claim: the other party to the claim,
              witnesses, experts, loss adjusters, solicitors and claims
              handlers.
            </li>
          </ul>
          <p>
            <strong>How is personal data used?</strong>
          </p>
          <p>
            We may need to use your personal data in order to carry out the
            following activities:
          </p>
          <ul>
            <li>
              To set you up as a new client (including carrying out ‘know your
              customer’ checks);
            </li>
            <li>To provide you with an insurance quote;</li>
            <li>To provide our products and services to you;</li>
            <li>To respond to your enquiries;</li>
            <li>To accept payments from you;</li>
            <li>To communicate with you about your policy;</li>
            <li>To renew your policy;</li>
            <li>To obtain reinsurance for your policy;</li>
            <li>To process insurance and reinsurance claims;</li>
            <li>For general insurance administration purposes;</li>
            <li>To comply with our legal and regulatory obligations;</li>
            <li>To model our risks;</li>
            <li>To defend or prosecute legal claims;</li>
            <li>To investigate or prosecute fraud;</li>
            <li>To respond to your enquiries;</li>
            <li>When you sign up for an online account</li>
            <li>
              To secure our network and our Website, debug the Website and
              repair errors;
            </li>
            <li>To conduct audits related to our interactions with you;</li>
            <li>
              To make our products and services better and to develop new
              products and services; and
            </li>
            <li>
              To send you notices and information regarding our products or
              services, including notifying you about special promotions or
              offers, where we are legally permitted to do so.
            </li>
          </ul>
          <p>
            <strong>Our legal basis for collecting your information</strong>
          </p>
          <p>
            Our legal basis for collecting and using your personal data will
            depend on the personal data concerned and the specific context in
            which we collect it.&nbsp;
          </p>
          <p>
            We will normally collect personal data where we need the information
            to provide you with our services / perform a contract with you,
            where the processing is in our legitimate interests and not
            overridden by your data protection interests or fundamental rights
            and freedoms, or with your consent.&nbsp;
          </p>
          <p>
            In some cases, we may use your personal data for a legal obligation,
            e.g. in order to complete ‘know your customer’ and money laundering
            checks before taking you on as a new client.&nbsp;
          </p>
          <p>
            If we ask you to provide personal information to comply with a legal
            requirement or to perform a contract with you, we will make this
            clear at the relevant time and advise you whether the provision of
            your personal information is mandatory (as well as of the possible
            consequences if you do not provide your personal information).&nbsp;
            You are under no obligation to provide personal data to us.&nbsp;
            However, if you should choose to withhold requested data, we may not
            be able to provide you with certain services.&nbsp;
          </p>
          <p>
            Similarly, if we collect and use your personal information in
            reliance on our legitimate interests (or those of any third party),
            we will make clear to you at the relevant time what those legitimate
            interests are.
          </p>
          <p>
            If you are a UK resident, we may collect and use your personal
            information, including sensitive personal information, on the basis
            of the insurance derogation in the UK Data Protection Act 2018.
          </p>
          <p>
            If you are an EU resident, we may collect and use your personal
            information, including sensitive personal information, on the basis
            of the substantial public interest of insurance purposes, as
            regulated in the General Data Protection Regulation (GDPR).&nbsp;
            &nbsp;If you have questions about or need further information
            concerning the legal basis on which we collect and use your personal
            information, please contact us using the contact details provided
            under the “Contact Us” section below.
          </p>
          <p>
            <strong>Who is your personal data shared with?</strong>
          </p>
          <p>
            We may disclose your personal information with the following
            categories of recipients.&nbsp;&nbsp;
          </p>
          <ul>
            <li>
              To our group companies, third party service providers and partners
              who provide data processing services (for example data hosting and
              storage companies, email marketing affiliates, and payment and
              claims processing companies) or who otherwise process personal
              information for purposes that are described in this Privacy Policy
              (see “How is personal data used?”).&nbsp; A list of our current
              group companies is available at
              http://www.tokiomarinehd.com/en/group/ and a list of our current
              service providers and partners may be available upon request;
            </li>
            <li>
              To any competent law enforcement body, regulatory, government
              agency, court or other third party where we believe disclosure is
              necessary (i) as a matter of applicable law or regulation, (ii) to
              exercise, establish or defend our legal rights, or (iii) to
              protect your vital interests or those of any other person;
            </li>
            <li>
              To a potential buyer (and its agents and advisers) in connection
              with any proposed purchase, merger or acquisition of any part of
              our business, provided that we inform the buyer it must use your
              personal information only for the purposes disclosed in this
              privacy policy;
            </li>
            <li>to any other person with your consent to the disclosure.</li>
          </ul>
          <p>
            We do not sell (or exchange) your personal information for monetary
            compensation.&nbsp;
          </p>
          <p>
            If you are a US resident, from time to time we may share your
            personal information with third parties for a third party’s own
            direct benefit and this type of sharing may be considered a sale
            under certain applicable laws.&nbsp; For more information on the
            type of information we may share in this manner, please{' '}
            <a href="https://www.suretybonds.com/contact.html">contact us</a>.
          </p>
          <p>
            <strong>International Transfers</strong>
          </p>
          <p>
            Your personal data may be transferred to, and processed in,
            countries other than the country in which you are resident.&nbsp;
            These countries may have data protection laws that are different to
            the laws of your country.
          </p>
          <p>
            Specifically, the servers of ContractorBonds.com are located in the
            United States.&nbsp; However, other TMHCC group companies are
            registered elsewhere, including in the EEA and operate around the
            world.&nbsp; This means that when we collect your information we may
            process it in any of these countries.
          </p>
          <p>
            We have taken appropriate safeguards to require that your personal
            data will remain protected in accordance with this privacy
            policy.&nbsp; These include implementing the European Commission’s
            Standard Contractual Clauses for transfers of personal information
            between our group companies, which require all group companies to
            protect personal information they process from the EEA in accordance
            with European Union data protection law.&nbsp;&nbsp;
          </p>
          <p>
            Our Standard Contractual Clauses can be provided on request.&nbsp;
            We have implemented similar appropriate safeguards with our third
            party service providers and partners and further details can be
            provided upon request.
          </p>
          <p>
            <strong>How long is personal information retained for?</strong>
          </p>
          <p>
            We will keep your personal data on our records for as long as we
            have an ongoing legitimate business need to do so.&nbsp; This
            includes providing you with a service you have requested from us or
            to comply with applicable legal, tax or accounting
            requirements.&nbsp; It also includes keeping your data for so long
            as there is any possibility that you or we may wish to bring a legal
            claim under your insurance contract, or where we are required to
            keep your data for legal or regulatory reasons.&nbsp; Please contact
            us using the contact details provided under the “Contact Us” section
            below should you require further information on our record retention
            procedures.
          </p>
          <p>
            We may also retain your personal data where such retention is
            necessary in order to protect your vital interests or the vital
            interests of another natural person.
          </p>
          <p>
            <strong>Your Rights as a Data Subject</strong>
          </p>
          <p>
            You have the right to opt-out of marketing communications we send
            you at any time.&nbsp; You can exercise this right by clicking on
            the “unsubscribe” or “opt-out” link in the marketing e-mails we send
            you or contacting us using the details provided under the
            “Marketing” heading below{' '}
            <a href="mailto:customercare@suretybonds.com">
              customercare@suretybonds.com
            </a>
            .
          </p>
          <p>
            If you are an EU resident, you will need to opt-in to receive
            marketing from us. If you then wish to unsubscribe at any point you
            can do so by clicking on the “unsubscribe” or “opt-out” link in the
            marketing e-mails we send you or contacting us using the details
            provided under the “Marketing” heading below.
          </p>
          <p>
            Similarly, if we have collected and processed your personal
            information with your consent, then you can withdraw your consent at
            any time.&nbsp; Withdrawing your consent will not affect the
            lawfulness of any processing we conducted prior to your withdrawal,
            nor will it affect processing of your personal information that was
            lawfully collected on grounds other than consent.
          </p>
          <p>
            You have the right to complain to a data protection authority about
            our collection and use of your personal information.&nbsp; For more
            information, please contact your local data protection authority.
            (Contact details for data protection authorities in the European
            Economic Area, Switzerland and certain non-European countries
            (including the US and Canada) are available{' '}
            <a href="https://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm">
              here
            </a>
            .)
          </p>
          <p>
            We respond to all requests we receive from individuals wishing to
            exercise their data protection rights in accordance with applicable
            data protection laws.
          </p>
          <p>
            You may exercise any of your rights in relation to your personal
            data by contacting us using the details set out in the “Contact us”
            section at the bottom of this page.&nbsp;
          </p>
          <p>
            If you are resident in the UK or EU, your principal rights under
            data protection law are as follows:
          </p>
          <p>a) the right to access;</p>
          <p>b) the right to rectification;</p>
          <p>c) the right to erasure;</p>
          <p>d) the right to restrict processing;</p>
          <p>e) the right to object to processing;</p>
          <p>f) the right to data portability;</p>
          <p>g) the right to complain to a supervisory authority; and</p>
          <p>h) the right to withdraw consent.</p>
          <p>
            If you wish to access, correct, update or request deletion of your
            personal information, we will ask you to provide us with a copy of
            any two of the following documents:&nbsp; Driver’s licence;
            Passport; Birth certificate; Bank statement (from the last 3
            months); or Utility bill (from the last 3 months).&nbsp; With
            regards to your right of access, the first access request will be
            complied with free of charge, but additional copies may be subject
            to a reasonable fee.
          </p>
          <p>
            In addition, you can object to processing of your personal
            information, ask us to restrict processing of your personal
            information or request portability of your personal information.
          </p>
          <p>
            If we have collected and processed your personal information with
            your consent, then you can withdraw your consent at any time.&nbsp;
            Withdrawing your consent will not affect the lawfulness of any
            processing we conducted prior to your withdrawal, nor will it affect
            processing of your personal information conducted in reliance on
            lawful processing grounds other than consent.
          </p>
          <p>
            You have the right to complain to a data protection authority about
            our collection and use of your personal information.&nbsp; For more
            information, please contact your local data protection authority. In
            the table below we have listed the contact details of the data
            protection authorities where we have local branches:
          </p>
          <div>
            <table className="w-full divide-y divide-gray-200" role="table">
              <thead>
                <tr>
                  <th
                    className="table-cell w-3/12 border-black border"
                    role="columnheader"
                  >
                    Country
                  </th>
                  <th
                    className="w-auto border-black border"
                    role="columnheader"
                  >
                    Data protection supervisory authority contact details
                  </th>
                </tr>
              </thead>
              <tbody className="shadow-sm">
                <tr>
                  <td role="cell" className="p-2">
                    UK
                  </td>
                  <td role="cell">
                    Information Commissioner’s Office
                    <br></br>Wycliffe House, Water Lane, Wilmslow, Cheshire SK9
                    5AF, UK<br></br>Tel: +44 (0) 303 123 1113<br></br>
                    Email: dpo@ico.org.uk<br></br>Website: http://www.ico.org.uk
                  </td>
                </tr>
                <tr>
                  <td role="cell">Belgium</td>
                  <td role="cell">
                    Autorité de la protection des données -
                    Gegevensbeschermingsautoriteit (APD-GBA)<br></br>Address:
                    Rue de la Presse 35 – Drukpersstraat 35, 1000 Bruxelles -
                    Brussel
                    <br></br>Tel.: +32 2 274 48 00<br></br>email:
                    contact@apd-gba.be<br></br>Website:
                    https://www.autoriteprotectiondonnees.be/ -
                    https://www.gegevensbeschermingsautoriteit.be/
                  </td>
                </tr>
                <tr>
                  <td role="cell">Germany</td>
                  <td role="cell">
                    Die Bundesbeauftragte für den Datenschutz und die
                    Informationsfreiheit<br></br>Address: Husarenstraße 30,
                    53117 Bonn<br></br>Tel.: +49 228 997799 0; +49 228 81995 0
                    <br></br>
                    email: poststelle@bfdi.bund.de<br></br>Website:
                    http://www.bfdi.bund.de/<br></br>The local German data
                    protection authorities’ contact details are available{' '}
                    <a href="https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html;jsessionid=E135EC903E28502A964CD9013C39DA75.1_cid319">
                      here
                    </a>
                    .
                  </td>
                </tr>
                <tr>
                  <td role="cell">France</td>
                  <td role="cell">
                    Commission Nationale de l’Informatique et des Libertés -
                    CNIL
                    <br></br>Address: 3 Place de Fontenoy, TSA 80715 – 75334
                    Paris, Cedex 07<br></br>Tel.: +33 1 53 73 22 22
                    <br></br>Website: http://www.cnil.fr/
                  </td>
                </tr>
                <tr>
                  <td role="cell">Spain</td>
                  <td role="cell">
                    Agencia Española de Protección de Datos (AEPD)<br></br>
                    Address: C/Jorge Juan, 6, 28001 Madrid<br></br>Tel.: +34 91
                    266 3517<br></br>email: internacional@aepd.es<br></br>
                    Website: https://www.aepd.es/
                  </td>
                </tr>
                <tr>
                  <td role="cell">Italy</td>
                  <td role="cell">
                    Garante per la protezione dei dati personali<br></br>
                    Address: Piazza di Monte Citorio, 121, 00186 Roma<br></br>
                    Tel.: +39 06 69677 1<br></br>email:
                    garante@garanteprivacy.it
                    <br></br>Website: http://www.garanteprivacy.it/
                  </td>
                </tr>
                <tr>
                  <td role="cell">Luxembourg</td>
                  <td role="cell">
                    Commission Nationale pour la Protection des Données
                    <br></br>Address: 1, avenue du Rock’n’Roll, L-4361
                    Esch-sur-Alzette<br></br>Tel.: +352 2610 60 1<br></br>
                    email: info@cnpd.lu<br></br>Website: http://www.cnpd.lu/
                  </td>
                </tr>
                <tr>
                  <td role="cell">Netherlands</td>
                  <td role="cell">
                    Autoriteit Persoonsgegevens<br></br>Address:
                    Bezuidenhoutseweg 30, P.O. Box 93374, 2509 AJ Den Haag/The
                    Hague<br></br>Tel.: +31 70 888 8500<br></br>Website:
                    https://autoriteitpersoonsgegevens.nl/nl
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Contact details for other data protection authorities in the
            European Economic Area, Switzerland and certain non-European
            countries (including the US and Canada) are available{' '}
            <a href="http://ec.europa.eu/newsroom/article29/item-detail.cfm?item_id=612080">
              here
            </a>
            &nbsp;.
          </p>
          <p>
            We respond to all requests we receive from individuals wishing to
            exercise their data protection rights in accordance with applicable
            data protection laws.
          </p>
          <p>
            If you are based in the UK, you may exercise any of your rights in
            relation to your personal data by contacting us using the email{' '}
            <a href="mailto:customercare@suretybonds.com">
              customercare@suretybonds.com
            </a>
            .
          </p>
          <p>
            If you are based in the EU, you may exercise any of your rights in
            relation to your personal data by contacting us using the email
            <a href="mailto:customercare@suretybonds.com">
              customercare@suretybonds.com
            </a>
            .
          </p>
          <p>
            Alternatively you can use the details set out in the “Contact us”
            section at the bottom of this page.
          </p>
          <p>
            If you are resident in the US, applicable law may entitle you, upon
            verifiable request, to receive disclosures relating to:
          </p>
          <ul>
            <li>
              The categories and specific pieces of information we have
              collected;
            </li>
            <li>
              The categories of sources from which the personal information is
              collected;
            </li>
            <li>
              The business or commercial purpose for collecting personal
              information; and
            </li>
            <li>
              The categories of third parties with whom we share personal
              information.
            </li>
          </ul>
          <p>
            If you are based in the EU, you may exercise any of your rights in
            relation to your personal data by contacting us{' '}
            <a href="https://app.onetrust.com/auth/login">here</a> or by calling
            us toll free at 888-688-0775.
          </p>
          <p>
            If you wish to exercise any of the rights described above, we will
            ask you to verify your identity.&nbsp; We generally will not charge
            to reply to your request, but we may charge a reasonable fee or
            refuse your request if the request is unjustified or
            excessive.&nbsp;
          </p>
          <p>
            <strong>Automated decision making</strong>
          </p>
          <p>
            In some instances, our use of your personal information may result
            in automated decisions being taken (including profiling) that
            legally affect you or similarly significantly affect
            you.&nbsp;&nbsp;
          </p>
          <p>
            Automated decisions mean that a decision concerning you is made
            automatically on the basis of a computer determination (using
            software algorithms), without our human review.&nbsp; For example,
            in certain instances we may use automated decisions to establish
            whether we will offer insurance coverage to a prospective
            insured.&nbsp; We have implemented measures to safeguard the rights
            and interests of individuals whose personal information is subject
            to automated decision-making.
          </p>
          <p>
            When we make an automated decision about you, you have the right to
            contest the decision, to express your point of view, and to require
            a human review of the decision.
          </p>
          <p>
            <strong>Marketing</strong>
          </p>
          <p>
            As mentioned above, we may use your personal data to send you
            marketing materials.&nbsp;
          </p>
          <p>
            If you are a UK or EU resident, you will need to opt-in to receive
            marketing from us. If you then wish to unsubscribe at any point you
            can do so by clicking on the “unsubscribe” or “opt-out” link in the
            marketing e-mails we send you or contacting us using the details
            provided under the “Marketing” heading below, or at{' '}
            <a href="mailto:customercare@suretybonds.com">
              customercare@suretybonds.com
            </a>
            .
          </p>
          <p>
            If you are resident in the US, you do not need to opt-in to receive
            marketing materials from us, and you have the right to opt-out of
            receiving such communications. If you would like to stop receiving
            marketing information from us, please unsubscribe by clicking the
            “unsubscribe” link in the relevant marketing email, email us at
            customercare@suretybonds.com or write to us at:
          </p>
          <p>SuretyBonds.com</p>
          <p>Attn: Customer Service</p>
          <p>3514 I-70 Dr. SE, Suite 102</p>
          <p>Columbia, MO 65201</p>
          <p>Telephone: 1 (800) 308-4358</p>
          <p>
            <strong>Security</strong>
          </p>
          <p>
            ContractorBonds.com is committed to keeping our customers’ data
            safe.&nbsp; We have security measures in place designed to protect
            against the loss, misuse and alteration of personal data under our
            control. For example, our security and technology policies are
            periodically reviewed and enhanced as necessary and only authorised
            personnel have access to user information.&nbsp; We use Secured
            Socket Layer (SSL) to encrypt financial information you input before
            it is sent to us.&nbsp; The servers we use to store personal data
            are kept in a secure environment.
          </p>
          <p>
            Although we cannot ensure or guarantee that loss, misuse or
            alteration of data will not occur, we use our best efforts to
            prevent this.&nbsp; If you have any concerns that your
            ContractorBonds.com account or personal data has been put at risk,
            please contact us.
          </p>
          <p>
            <strong>Cookies</strong>
          </p>
          <p>
            Our sites use cookies (a small piece of information that is placed
            on your computer when you visit certain websites) to distinguish you
            from other users, to track your browsing pattern and to build a
            profile of how you and other users use our sites. This helps us to
            provide you with a good experience when you browse any of our sites
            and also allows us to improve our sites. If you have an online
            account with us, ContractorBonds.com also uses cookies to recognize
            you to pre-fill forms to save you time. TMHCC does not mandate
            Cookies for you to access our sites and you may freely set your
            browser to reject all Cookies or prompt you to accept or reject
            them. Some of the cookies we use are session cookies and only last
            until you close your browser, others are persistent cookies which
            are stored on your computer for longer. We may collect information
            through web beacons about your web browsing activities such as the
            address of the page you are visiting, the address of the referrer
            page you had previously visited, the time you are viewing the page,
            your browsing environment and your display settings. We do this in
            order to optimize your browsing experience, the use of web-based
            services and provide you with relevant information on
            ContractorBonds.com products and services when you have opted-in to
            receiving such correspondence.&nbsp; For further information about
            ContractorBonds.com’s use of cookies, see ContractorBonds.com’s
            Cookie Policy.&nbsp;
          </p>
          <p>
            <strong>Tracking</strong>
          </p>
          <p>
            ContractorBonds.com will not respond to Web browser “do not track”
            signals. &nbsp;If you would like additional information about online
            tracking and various opt-out mechanisms, please see
            https://youradchoices.com/&nbsp;&nbsp;
          </p>
          <p>
            Because we link to social media sites, and from time to time may
            include third-party advertisements, other parties may collect your
            personally identifiable information about your online activities
            over time and across different web sites when you visit this Site.
          </p>
          <p>
            Please note that not all tracking will stop even if you delete
            cookies.
          </p>
          <p>
            <strong>Children’s data</strong>
          </p>
          <p>
            Our websites and applications are not directed to children under 16
            and we do not knowingly collect any personal information directly
            from children under 16. If you believe that we are processing
            personal information pertaining to a child inappropriately, we ask
            you to contact us using the information provided under the “Contact
            Us” section.
          </p>
          <p>
            <strong>Changes to this Privacy Policy</strong>
          </p>
          <p>
            We may update this Privacy Policy from time to time in response to
            changing legal, technical or business developments. When we update
            our Privacy Policy, we will take appropriate measures to inform you,
            consistent with the significance of the changes we make.&nbsp; We
            will obtain your consent to any material Privacy Policy changes if
            and where this is required by applicable data protection laws.
          </p>
          <p>
            You can see when this Privacy Policy was last updated by checking
            the “effective date” displayed at the top of this Privacy
            Policy.&nbsp;
          </p>
          <p>
            Any changes will be effective only after the effective date of the
            change and will not affect any dispute arising prior to the
            effective date of the change.
          </p>
          <p>
            <strong>Contact us</strong>
          </p>
          <p>
            If you have any questions about this Privacy Policy, please contact
            us using the following contact details:
          </p>
          <p>Customer Service</p>
          <p>SuretyBonds.com</p>
          <p>3514 I-70 Dr. SE, Suite 102</p>
          <p>Columbia, MO 65201</p>
          <p>Residents in the US can also call us toll free at: 888-688-0775</p>
        </div>
      </div>
    </>
  );
}
